import React from 'react'
import { 
  AddCircle
} from '@mui/icons-material'
import { 
  defaultMetrics 
} from '../utils'
import { 
  DefaultMetricType, 
  DefaultMetricsType,
  MetricTemplate
} from '../types'
import { CustomDefinition } from '../../../interfaces/db'
import { Typography } from '../../../interfaces/typography'

interface QuickTemplatesProps {
  templateTab: string
  setTemplateTab: (tab: string) => void
  addNewDefinition: (template?: Partial<CustomDefinition> | MetricTemplate) => void
}

export function QuickTemplates({ 
  templateTab, 
  setTemplateTab, 
  addNewDefinition 
}: QuickTemplatesProps) {
  return (
    <div className="h-full flex flex-col">
      <div className="bg-white border border-gray-200 rounded-xl p-6 h-full flex flex-col shadow-sm">
        {/* Header */}
        <div className="mb-6">
          <Typography variant="h2" color="#1F2937" fontWeight={600} className="mb-2">
            Quick Add Templates
          </Typography>
          <Typography variant="mediumParagraph" color="#6B7280">
            Get started quickly with pre-configured metrics
          </Typography>
        </div>
        
        {/* Clean tab buttons */}
        <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-lg w-fit">
          <button
            onClick={() => setTemplateTab('sales')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
              templateTab === 'sales' 
                ? 'bg-white text-blue-600 shadow-sm font-semibold' 
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            Sales
          </button>
          <button
            onClick={() => setTemplateTab('qa')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
              templateTab === 'qa' 
                ? 'bg-white text-blue-600 shadow-sm font-semibold' 
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            QA
          </button>
        </div>

        {/* Templates grid */}
        <div className="flex-1 overflow-auto">
          <div className="space-y-3">
            {defaultMetrics[templateTab as keyof DefaultMetricsType].map((metric: DefaultMetricType, index: number) => (
              <div 
                key={index}
                onClick={() => addNewDefinition(metric)}
                className="group cursor-pointer bg-gradient-to-r from-gray-50 to-white border border-gray-200 rounded-lg p-4 transition-all duration-200 hover:border-blue-300 hover:shadow-md hover:-translate-y-0.5 hover:from-blue-50 hover:to-indigo-50"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <Typography variant="h4" color="#2563EB" fontWeight={600} className="mb-2 group-hover:text-blue-700 transition-colors">
                      {metric.label}
                    </Typography>
                    <Typography variant="smallParagraph" color="#6B7280" className="line-clamp-2 group-hover:text-gray-700 transition-colors">
                      {metric.description}
                    </Typography>
                  </div>
                  <div className="ml-3 opacity-60 group-hover:opacity-100 transition-all duration-200 group-hover:scale-110">
                    <div className="flex items-center justify-center w-8 h-8 bg-blue-600 text-white rounded-full shadow-sm group-hover:shadow-md">
                      <AddCircle sx={{ fontSize: 16 }} />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer tip */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          <div className="flex items-center gap-2 text-gray-500">
            <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
            <Typography variant="smallParagraph" color="#6B7280">
              Click any template to customize and add to your metrics
            </Typography>
          </div>
        </div>
      </div>
    </div>
  )
} 